import {Injectable} from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';

@Injectable()
export class RequestService {
    private cache = new Map<string, { data: any; timestamp: number; ttl: number }>();
    private readonly DEFAULT_CACHE_TTL = 30000; // 30秒のキャッシュ

    constructor(private http: HttpClient, private router: Router) {
    }

    private getCacheKey(path: string, params: any = {}): string {
        const sortedParams = Object.keys(params).sort().reduce((result, key) => {
            // undefinedや空文字列のパラメータは無視
            if (params[key] !== undefined && params[key] !== '' && params[key] !== 'undefined') {
                result[key] = params[key];
            }
            return result;
        }, {} as any);
        return `${path}?${JSON.stringify(sortedParams)}`;
    }

    private isValidCache(cacheEntry: any): boolean {
        return cacheEntry && (Date.now() - cacheEntry.timestamp < cacheEntry.ttl);
    }

    private setCache(key: string, data: any, ttl: number = this.DEFAULT_CACHE_TTL): void {
        this.cache.set(key, {
            data: data,
            timestamp: Date.now(),
            ttl: ttl
        });
    }

    private getFromCache(key: string): any | null {
        const cacheEntry = this.cache.get(key);
        if (this.isValidCache(cacheEntry)) {
            console.log(`📦 Cache HIT: ${key}`);
            return cacheEntry!.data;
        }
        
        // 期限切れのキャッシュを削除
        if (cacheEntry) {
            this.cache.delete(key);
        }
        return null;
    }

    private getHeaders(): HttpHeaders {
        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*'
        });

        // Add authorization token if available
        const token = localStorage.getItem('accessToken');
        if (token) {
            headers = headers.set('Authorization', `Bearer ${token}`);
        }

        return headers;
    }

    private get options(): any {
        return {
            headers: this.getHeaders(),
            withCredentials: true
        };
    }

    get(path: string, data = {}, useCache: boolean = true): Promise<any> {
        // 段階的修正: まずパラメータ清浄化のみ実装
        const cleanParams = this.cleanParameters(data);

        let finalPath = path;
        if (Object.keys(cleanParams).length > 0) {
            const queryString = Object.keys(cleanParams)
                .map(key => {
                    const value = cleanParams[key];
                    // 配列やオブジェクトの場合はJSON文字列化してからエンコード
                    // これにより member_number: ["1234"] が正しく %5B%221234%22%5D になる
                    const stringValue = (typeof value === 'object' && value !== null)
                        ? JSON.stringify(value)
                        : String(value);
                    return `${encodeURIComponent(key)}=${encodeURIComponent(stringValue)}`;
                })
                .join('&');
            finalPath = `${path}?${queryString}`;
        }

        if (this.isIE()) {
            finalPath = encodeURI(finalPath);
        }

        console.log(`🔗 API CALL: GET ${finalPath}`);

        return this.http.get(this.getUri(finalPath), this.options)
            .toPromise()
            .then(response => {
                console.log(`✅ API SUCCESS: GET ${finalPath}`);
                return response;
            })
            .catch(error => {
                console.error(`❌ API ERROR: GET ${finalPath}`, error);
                return this.handleError(error);
            });
    }

    /**
     * パラメータ清浄化（段階的修正の最初のステップ）
     */
    private cleanParameters(data: any): any {
        return Object.keys(data).reduce((result, key) => {
            const value = data[key];
            // 段階3: より厳密なパラメータ清浄化
            if (value !== undefined && 
                value !== '' && 
                value !== 'undefined' && 
                value !== null &&
                value !== 'null' &&
                !(typeof value === 'string' && value.trim() === '')) {
                result[key] = value;
            }
            return result;
        }, {} as any);
    }

    post(path: string, data: any = {}): Promise<any> {

        return this.http
            .post(this.getUri(path), data, this.options)
            .toPromise()
            .then(res => res)
            .catch(this.handleError.bind(this));
    }

    put(path: string, data: any): Promise<any> {
        data._method = 'put';
        return this.http
            .post(this.getUri(path), data, this.options)
            .toPromise()
            .then(res => res)
            .catch(this.handleError.bind(this));
    }

    delete(path: string, data: any = {}): Promise<any> {
        data._method = 'DELETE';
        return this.http
            .post(this.getUri(path), data, this.options)
            .toPromise()
            .then(res => res)
            .catch(this.handleError.bind(this));
    }

    private handleError(error: any) {
        console.error('An error occurred', error);
        
        // Handle 401 unauthorized errors
        if (error.status === 401) {
            console.log('Unauthorized access, redirecting to login...');
            // Clear auth tokens
            localStorage.removeItem('accessToken');
            localStorage.removeItem('dashboardActive');
            // Redirect to login page
            this.router.navigate(['/login']);
        }
        
        return Promise.reject(error.message || error);
    }

    getUri(path) {
        let uri = '/';
        // Check for localhost development environment
        if (window.location.host.indexOf('localhost') >= 0 || window.location.host.indexOf('127.0.0.1') >= 0) {
            // Use backend server on port 8120 for local development
            uri = 'http://localhost:8120/';
        } else if (window.location.host.indexOf('dev2') >= 0) {
            // dev2を先にチェック（dev2にはdevが含まれるため）
            uri = 'https://apidev2.festaria-members.jp/';
        } else if (window.location.host.indexOf('dev') >= 0) {
            uri = 'https://apidev.festaria-members.jp/';
        } else if (window.location.host.indexOf('stg') >= 0) {
            uri = 'https://apistg.festaria-members.jp/';
        } else if (window.location.host.indexOf('festaria-members.jp') >= 0) {
            uri = 'https://apiprod.festaria-members.jp/';
        }
        return uri + path;
    }

    /**
     * Blobレスポンスをダウンロード（CSV/PDF等のファイルダウンロード用）
     * @param path APIパス
     * @param data クエリパラメータ
     * @param filename ダウンロードファイル名
     */
    downloadFile(path: string, data = {}, filename?: string): Promise<void> {
        const cleanParams = this.cleanParameters(data);

        let finalPath = path;
        if (Object.keys(cleanParams).length > 0) {
            const queryString = Object.keys(cleanParams)
                .map(key => {
                    const value = cleanParams[key];
                    // 配列やオブジェクトの場合はJSON文字列化してからエンコード
                    const stringValue = (typeof value === 'object' && value !== null)
                        ? JSON.stringify(value)
                        : String(value);
                    return `${encodeURIComponent(key)}=${encodeURIComponent(stringValue)}`;
                })
                .join('&');
            finalPath = `${path}?${queryString}`;
        }

        console.log(`📥 FILE DOWNLOAD: GET ${finalPath}`);

        const headers = this.getHeaders();

        return this.http.get(this.getUri(finalPath), {
            headers: headers,
            withCredentials: true,
            responseType: 'blob',
            observe: 'response'
        })
            .toPromise()
            .then(response => {
                if (!response || !response.body) {
                    throw new Error('Empty response');
                }

                // Content-Dispositionヘッダーからファイル名を取得（存在する場合）
                let downloadFilename = filename;
                if (!downloadFilename) {
                    const contentDisposition = response.headers.get('Content-Disposition');
                    if (contentDisposition) {
                        const matches = /filename="?([^"]+)"?/.exec(contentDisposition);
                        if (matches && matches[1]) {
                            downloadFilename = matches[1];
                        }
                    }
                }

                // デフォルトファイル名（レガシーPHP準拠: users_YmdHis.csv）
                if (!downloadFilename) {
                    const now = new Date();
                    const year = now.getFullYear();
                    const month = String(now.getMonth() + 1).padStart(2, '0');
                    const day = String(now.getDate()).padStart(2, '0');
                    const hour = String(now.getHours()).padStart(2, '0');
                    const minute = String(now.getMinutes()).padStart(2, '0');
                    const second = String(now.getSeconds()).padStart(2, '0');
                    downloadFilename = `users_${year}${month}${day}${hour}${minute}${second}.csv`;
                }

                // Blobをダウンロード
                const blob = response.body;
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = downloadFilename;
                link.click();
                window.URL.revokeObjectURL(url);

                console.log(`✅ FILE DOWNLOADED: ${downloadFilename}`);
            })
            .catch(error => {
                console.error(`❌ DOWNLOAD ERROR: GET ${finalPath}`, error);
                return this.handleError(error);
            });
    }

    private isIE() {
        // 获取当前浏览器相关信息
        const explorer = window.navigator.userAgent.toLowerCase();
        // 判断是否为ie浏览器
        return explorer.indexOf('msie') >= 0 || explorer.indexOf('rv:11.0) like gecko') >= 0;
    }
}
